// Emergency / printable basketball score sheet — generated 100% client-side.
// No server request is needed: it uses the game + roster data already held in
// memory (and cached logos) so it works with no internet connection.

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { loadImageDataUrl } from "@/lib/offlineStore";

const ORANGE = [255, 87, 34];
const DARK = [20, 20, 20];
const GREY = [120, 120, 120];

const fmtDate = (iso) => {
  if (!iso) return "____________________";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso);
  return d.toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" });
};
const fmtTime = (iso) => {
  if (!iso) return "____________";
  const d = new Date(iso);
  if (isNaN(d)) return "____________";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};
// A short, human-friendly Game ID derived from the game's UUID + date.
const gameCode = (game) => {
  if (!game) return "____________________";
  const year = game.date ? new Date(game.date).getFullYear() : new Date().getFullYear();
  const tail = String(game.id || "").replace(/[^a-zA-Z0-9]/g, "").slice(-5).toUpperCase() || "00000";
  return `GAME-${year}-${tail}`;
};

function header(doc, subtitle) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...DARK);
  doc.rect(0, 0, w, 62, "F");
  doc.setTextColor(...ORANGE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.text("IEFSC BASKETBALL", w / 2, 30, { align: "center" });
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(subtitle, w / 2, 48, { align: "center" });
}

function gameInfoBlock(doc, game, startY) {
  const left = 40;
  const rows = [
    ["Game ID", gameCode(game)],
    ["Date", fmtDate(game?.date)],
    ["Time", fmtTime(game?.date)],
    ["Venue", game?.venue || "____________________"],
    ["Court", game?.court || "__________"],
    ["Division / League", game?.division || "____________________"],
    ["Week", game?.week != null ? `Week ${game.week}` : "__________"],
    ["Officials", "________________________________"],
  ];
  autoTable(doc, {
    startY,
    margin: { left, right: 40 },
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 3, textColor: DARK, lineColor: [200, 200, 200] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 95, fillColor: [245, 245, 245] },
      1: { cellWidth: 165 },
      2: { fontStyle: "bold", cellWidth: 95, fillColor: [245, 245, 245] },
      3: { cellWidth: "auto" },
    },
    body: [
      [rows[0][0], rows[0][1], rows[1][0], rows[1][1]],
      [rows[2][0], rows[2][1], rows[3][0], rows[3][1]],
      [rows[4][0], rows[4][1], rows[5][0], rows[5][1]],
      [rows[6][0], rows[6][1], rows[7][0], rows[7][1]],
    ],
  });
  return doc.lastAutoTable.finalY;
}

function tryLogo(doc, logoUrl, x, y, size) {
  if (!logoUrl) return false;
  const dataUrl = loadImageDataUrl(logoUrl);
  if (!dataUrl) return false;
  try {
    const fmt = dataUrl.includes("image/png") ? "PNG" : "JPEG";
    doc.addImage(dataUrl, fmt, x, y, size, size);
    return true;
  } catch {
    return false;
  }
}

function teamSection(doc, label, team, roster, startY, current) {
  const left = 40;
  const w = doc.internal.pageSize.getWidth();
  // Team banner
  doc.setFillColor(...ORANGE);
  doc.rect(left, startY, w - 80, 22, "F");
  const hasLogo = tryLogo(doc, team?.logo_url, left + 3, startY + 3, 16);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`${label}: ${(team?.name || "____________________").toUpperCase()}`, left + (hasLogo ? 24 : 8), startY + 15);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(`Coach: ${team?.coach || "______________"}   Asst: ______________`, w - 44, startY + 15, { align: "right" });

  const head = [["#", "Player Name", "Pos", "1Q", "2Q", "3Q", "4Q", "OT", "Total", "Fouls"]];
  const body = (roster || []).map((p) => {
    const st = current && current.byPlayer ? current.byPlayer[p.id] : null;
    return [
      p.number != null && p.number !== "" ? String(p.number) : "",
      p.name || "",
      p.position || "",
      "", "", "", "", "",
      st ? String(st.pts ?? "") : "",
      st ? String(st.pf ?? "") : "",
    ];
  });
  // Pad to at least 10 rows so there is space to add walk-up players by hand.
  const minRows = 10;
  while (body.length < minRows) body.push(["", "", "", "", "", "", "", "", "", ""]);

  autoTable(doc, {
    startY: startY + 22,
    margin: { left, right: 40 },
    theme: "grid",
    head,
    body,
    styles: { fontSize: 9, cellPadding: 4, textColor: DARK, lineColor: [180, 180, 180], minCellHeight: 16 },
    headStyles: { fillColor: DARK, textColor: [255, 255, 255], fontStyle: "bold", halign: "center", fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 26, halign: "center", fontStyle: "bold" },
      1: { cellWidth: "auto" },
      2: { cellWidth: 30, halign: "center" },
      3: { cellWidth: 26, halign: "center" },
      4: { cellWidth: 26, halign: "center" },
      5: { cellWidth: 26, halign: "center" },
      6: { cellWidth: 26, halign: "center" },
      7: { cellWidth: 26, halign: "center" },
      8: { cellWidth: 40, halign: "center", fontStyle: "bold" },
      9: { cellWidth: 34, halign: "center" },
    },
  });
  let y = doc.lastAutoTable.finalY;
  // Team fouls / timeouts strip
  const tf = current && current.teamFouls ? current.teamFouls[label === "HOME" ? "home" : "away"] : null;
  const to = current && current.timeouts ? current.timeouts[label === "HOME" ? "home" : "away"] : null;
  autoTable(doc, {
    startY: y,
    margin: { left, right: 40 },
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 3, textColor: DARK, lineColor: [180, 180, 180] },
    columnStyles: {
      0: { fontStyle: "bold", fillColor: [245, 245, 245], cellWidth: 90 },
      1: { cellWidth: 60, halign: "center" },
      2: { fontStyle: "bold", fillColor: [245, 245, 245], cellWidth: 90 },
      3: { cellWidth: "auto", halign: "center" },
    },
    body: [[
      "Team Fouls", tf != null ? String(tf) : "",
      "Timeouts", to != null ? String(to) : "",
    ]],
  });
  return doc.lastAutoTable.finalY;
}

function scoreSummary(doc, game, home, away, current, startY) {
  const left = 40;
  const homeName = (home?.name || "HOME").toUpperCase();
  const awayName = (away?.name || "AWAY").toUpperCase();
  const hFinal = current ? String(current.homeScore ?? "") : "";
  const aFinal = current ? String(current.awayScore ?? "") : "";
  autoTable(doc, {
    startY,
    margin: { left, right: 40 },
    theme: "grid",
    head: [["Quarter Scores", "1Q", "2Q", "3Q", "4Q", "OT", "FINAL"]],
    styles: { fontSize: 9, cellPadding: 5, textColor: DARK, lineColor: [180, 180, 180], minCellHeight: 18, halign: "center" },
    headStyles: { fillColor: DARK, textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
    columnStyles: { 0: { fontStyle: "bold", halign: "left", cellWidth: 150, fillColor: [245, 245, 245] }, 6: { fontStyle: "bold" } },
    body: [
      [homeName, "", "", "", "", "", hFinal],
      [awayName, "", "", "", "", "", aFinal],
    ],
  });
  return doc.lastAutoTable.finalY;
}

function notesAndSignatures(doc, startY) {
  const left = 40;
  const w = doc.internal.pageSize.getWidth();
  let y = startY + 10;
  if (y > doc.internal.pageSize.getHeight() - 150) { doc.addPage(); y = 50; }
  doc.setTextColor(...DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("NOTES", left, y);
  doc.setDrawColor(200, 200, 200);
  for (let i = 0; i < 3; i++) {
    y += 18;
    doc.line(left, y, w - 40, y);
  }
  y += 34;
  const colW = (w - 80) / 3;
  const sigLine = (x, label) => {
    doc.setDrawColor(80, 80, 80);
    doc.line(x, y, x + colW - 20, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...GREY);
    doc.text(label, x, y + 12);
  };
  sigLine(left, "Scorekeeper Signature");
  sigLine(left + colW, "Referee 1 Signature");
  sigLine(left + colW * 2, "Referee 2 Signature");
}

function footer(doc) {
  const pages = doc.internal.getNumberOfPages();
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...GREY);
    doc.setFont("helvetica", "normal");
    doc.text("IEFSC Basketball — Official Emergency Score Sheet", 40, h - 20);
    doc.text(`Page ${i} of ${pages}`, w - 40, h - 20, { align: "right" });
  }
}

// data: { game, homeTeam, awayTeam, homeRoster, awayRoster }
// current: null for blank, or { homeScore, awayScore, byPlayer:{[pid]:{pts,pf}}, teamFouls:{home,away}, timeouts:{home,away} }
function build(data, current) {
  const doc = new jsPDF({ unit: "pt", format: "letter" }); // 612 x 792 pt
  const subtitle = current ? "CURRENT GAME SCORE SHEET" : "EMERGENCY SCORE SHEET (BLANK)";
  header(doc, subtitle);
  let y = gameInfoBlock(doc, data.game, 74);
  y = teamSection(doc, "HOME", data.homeTeam, data.homeRoster, y + 12, current);
  y = teamSection(doc, "AWAY", data.awayTeam, data.awayRoster, y + 12, current);
  y = scoreSummary(doc, data.game, data.homeTeam, data.awayTeam, current, y + 12);
  notesAndSignatures(doc, y);
  footer(doc);
  return doc;
}

const fileName = (game, mode) => {
  const code = gameCode(game).replace(/[^A-Z0-9-]/g, "");
  return `IEFSC-${mode}-${code}.pdf`;
};

export function downloadBlankScoreSheet(data) {
  const doc = build(data, null);
  doc.save(fileName(data.game, "BLANK"));
}

export function downloadCurrentGamePdf(data, current) {
  const doc = build(data, current);
  doc.save(fileName(data.game, "CURRENT"));
}

// Open the PDF in a new tab and trigger the browser print dialog (offline-safe: uses a blob URL).
export function printScoreSheet(data, current) {
  const doc = build(data, current);
  doc.autoPrint();
  const url = doc.output("bloburl");
  const win = window.open(url, "_blank");
  if (!win) {
    // Popup blocked — fall back to downloading so the sheet is never lost.
    doc.save(fileName(data.game, current ? "CURRENT" : "BLANK"));
  }
}
